import { Button } from "@studyplus/boron-ui";
import * as React from "react";
import Loader from "../../../components/atoms/Loader";
import { Modal } from "../../../components/atoms/Modal";
import { SelectWrapper } from "../../../components/general/SelectWrapper";
import { Text } from "../../../components/general/Text";
import { COURSES_FETCH_FAILED_MESSAGE, NO_COURSE_MESSAGE } from "./messages";
import { StudentSearch } from "./StudentSearch";
import {
  type IssuableStudent,
  type LearningCoachCourse,
  toIssuableStudentLabel,
} from "./types";
import { useFetchLearningCoachCourses } from "./useFetchLearningCoachCourses";
import { useIssueApplicationRequest } from "./useIssueApplicationRequest";

type Props = {
  sectionId: string;
};

// 申込 URL の発行（起票）。「＋ 申込URL発行」から開くモーダルで、入力と確認を切り替える
export const ApplicationRequestIssuance = ({ sectionId }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>＋ 申込URL発行</Button>
      {isOpen && (
        <IssuanceModal sectionId={sectionId} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
};

const IssuanceModal = ({
  sectionId,
  onClose,
}: {
  sectionId: string;
  onClose: () => void;
}) => {
  const [step, setStep] = React.useState<"input" | "confirm">("input");
  const [student, setStudent] = React.useState<IssuableStudent | null>(null);
  const [courseId, setCourseId] = React.useState<number | null>(null);

  const courses = useFetchLearningCoachCourses({ sectionId, enabled: true });
  const issuance = useIssueApplicationRequest({
    sectionId,
    onSuccess: onClose,
  });

  // 選択肢が 1 つのときは初期選択にする
  React.useEffect(() => {
    if (courses.data?.length === 1) {
      setCourseId(courses.data[0].id);
    }
  }, [courses.data]);

  const course = courses.data?.find(({ id }) => id === courseId) ?? null;

  // 結果が返るまでは閉じさせない（ISS-06）
  const handleClose = () => {
    if (!issuance.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={handleClose}
      className="mx-auto my-16 w-[90%] max-w-[56rem] rounded-lg"
    >
      {step === "input" ? (
        <Modal.Header onClose={handleClose}>
          <h2>申込URL発行</h2>
        </Modal.Header>
      ) : (
        <Modal.NoCloseHeader>
          <h2>申込URL発行内容の確認</h2>
        </Modal.NoCloseHeader>
      )}
      <Modal.Body>
        {/* 確認から戻ったときに検索の条件と結果を残すため、入力は確認の間も隠すだけにする */}
        <div hidden={step !== "input"}>
          <InputStep
            sectionId={sectionId}
            courses={courses}
            student={student}
            onSelectStudent={setStudent}
            course={course}
            onSelectCourse={setCourseId}
            onCancel={handleClose}
            onConfirm={() => setStep("confirm")}
          />
        </div>
        {step === "confirm" && student && course && (
          <ConfirmStep
            student={student}
            course={course}
            isIssuing={issuance.isPending}
            onBack={() => setStep("input")}
            onIssue={() =>
              issuance.mutate({ studentId: student.id, courseId: course.id })
            }
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

const InputStep = ({
  sectionId,
  courses,
  student,
  onSelectStudent,
  course,
  onSelectCourse,
  onCancel,
  onConfirm,
}: {
  sectionId: string;
  courses: ReturnType<typeof useFetchLearningCoachCourses>;
  student: IssuableStudent | null;
  onSelectStudent: (student: IssuableStudent | null) => void;
  course: LearningCoachCourse | null;
  onSelectCourse: (courseId: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const renderForm = () => {
    if (courses.isPending) {
      return <Loader loading />;
    }
    // コースを選べなければ、生徒もコースも選ばせない（ISS-01）
    if (courses.error || !courses.data) {
      return <Text color="red">{COURSES_FETCH_FAILED_MESSAGE}</Text>;
    }
    if (courses.data.length === 0) {
      return <Text>{NO_COURSE_MESSAGE}</Text>;
    }
    const options = courses.data.map(({ id, name }) => ({
      value: id,
      label: name,
    }));
    return (
      <div className="flex flex-col gap-8">
        <section className="flex flex-col gap-4">
          <h3 className="m-0 text-sm text-gray-600">生徒</h3>
          <StudentSearch
            sectionId={sectionId}
            selectedStudent={student}
            onSelect={onSelectStudent}
          />
        </section>
        <section className="flex flex-col gap-4">
          <h3 className="m-0 text-sm text-gray-600">コース</h3>
          {/* 選択肢が 1 つでも常にプルダウンを出す */}
          <SelectWrapper
            aria-label="コース"
            placeholder="コースを選択"
            options={options}
            value={options.find(({ value }) => value === course?.id) ?? null}
            onChange={(option) => {
              if (option) {
                onSelectCourse(Number(option.value));
              }
            }}
            isSearchable={false}
          />
        </section>
        <div className="rounded bg-blue-100 p-6 text-sm">
          <p className="m-0">
            申込フォームURLは、保護者のLINEへ起票元校舎から送られます。
          </p>
          <p className="m-0 text-gray-600">
            未連携の場合は連携が済み次第、自動で送られます（先生の送信操作は不要）。
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-8">
      {renderForm()}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={onCancel}>
          キャンセル
        </Button>
        <Button disabled={!student || !course} onClick={onConfirm}>
          確認する
        </Button>
      </div>
    </div>
  );
};

const ConfirmStep = ({
  student,
  course,
  isIssuing,
  onBack,
  onIssue,
}: {
  student: IssuableStudent;
  course: LearningCoachCourse;
  isIssuing: boolean;
  onBack: () => void;
  onIssue: () => void;
}) => (
  <div className="flex flex-col gap-8">
    <p className="m-0 text-sm">以下の内容で申込URLを発行します。</p>
    <dl className="m-0 flex flex-col gap-4 rounded border border-solid border-gray-400 px-8 py-6">
      <div className="flex gap-8">
        <dt className="w-[8rem] text-sm text-gray-600">生徒</dt>
        <dd className="m-0">{toIssuableStudentLabel(student)}</dd>
      </div>
      <div className="flex gap-8">
        <dt className="w-[8rem] text-sm text-gray-600">コース</dt>
        <dd className="m-0">{course.name}</dd>
      </div>
    </dl>
    {/* 同じ生徒にこの校舎から発行した保護者申込待ちの URL があれば、再発行の注意を添える（ISS-05） */}
    {student.hasAwaitingGuardianApplication && (
      <p className="m-0 rounded border border-solid border-yellow-400 p-6 text-sm">
        同じ生徒に保護者申込待ちのURLがあります。再発行しますか？
      </p>
    )}
    <p className="m-0 rounded bg-blue-100 p-6 text-sm">
      発行後、先生からは取り消せません。
    </p>
    <div className="flex justify-end gap-4">
      <Button variant="outline" disabled={isIssuing} onClick={onBack}>
        戻る
      </Button>
      {/* 結果が返るまで押せなくする（ISS-06） */}
      <Button disabled={isIssuing} isLoading={isIssuing} onClick={onIssue}>
        発行する
      </Button>
    </div>
  </div>
);
